import "./formDropdown.scss";

import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { PopoverBody } from "reactstrap";

import { withScreenContext } from "../../components/screen/ScreenContext";
import KeyConstants from "../../constants/keyConstants";
import { isScreenModeMobile } from "../../constants/screenModes";
import { PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";
import CheckboxListView from "../checkboxList/CheckboxListView";
import { withContainerContext } from "../container/ContainerContext";
import Icon from "../interface/Icon";
import NavigationToolbar from "../navigationToolbar";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import { getDropdownOptions } from "./utils";

const SAFE_SPACE = "__SPACE__";
const SAFE_NULL = "__NULL__";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();

export const renderOptionsLabel = options =>
  options.map((opt, index) => (index > 0 ? ", " : "") + getLabelWithUntitled(opt.label));

class FormCheckboxDropdown extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { showMenu: false, options: null };
    this._domNode = null;
    this.getSelectedOptions = this.getSelectedOptions.bind(this);
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.toggle = this.toggle.bind(this);
    this.onToggleMenu = this.onToggleMenu.bind(this); // Passing to menu component
    this.renderMenu = this.renderMenu.bind(this);
    this.renderControl = this.renderControl.bind(this);
    this._dropdownId = "x" + uniqueId();
  }

  componentDidMount() {
    if (this.props.initialShowMenu) {
      this.showMenu();
    }
  }

  getSelectedOptions(props) {
    const options = getDropdownOptions(props.options);
    return options.filter(option => option.selected);
  }

  toggle() {
    if (this.state.showMenu) {
      this.hideMenu();
    } else {
      this.showMenu();
    }
  }

  getListItemDomId(opt) {
    const val = opt.value ? `${opt.value.toString()}`.replace(/\s/g, SAFE_SPACE) : SAFE_NULL;
    return `${this._dropdownId}-${val}`;
  }

  onKeyDown(e) {
    if (!this.state.showMenu) {
      // Not currently open.
      // So open it with "enter"
      switch (e.key) {
        case KeyConstants.Enter:
          this.showMenu();
          break;
        default:
          break;
      }
    }
  }

  onBlur(_e) {
    // If we don't have a search field, then any blur means we are moving to another control.
    // if (!this.props.allowSearch) {
    //   this.selectHighlightedOption();
    // }
  }

  showMenu() {
    this.setState({
      showMenu: true
    });
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(true);
    }
  }

  hideMenu() {
    this.setState({ showMenu: false });
    if (this._domNode) {
      this._domNode.focus();
    }
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(false);
    }
  }

  focus() {
    this._domNode.focus();
    if (this.props.openOnFocus) {
      this.showMenu();
    }
  }

  onToggleMenu(value) {
    if (value) {
      this.showMenu();
    } else {
      this.hideMenu();
    }
  }

  getMenuStyle() {
    let style = this.props.style ? { ...this.props.style } : {};

    if (isDesktop) {
      style.width =
        this.props.useParentWidth && this._domNode && this._domNode.clientWidth
          ? `${this._domNode.clientWidth}px`
          : this.props.style && this.props.style.width
            ? this.props.style.width
            : `auto`;
    }

    return style;
  }

  renderMenu() {
    if (!this.props.disabled) {
      return (
        <div style={this.props.style}>
          <CheckboxListView items={this.props.options} onChange={this.props.onChange} />
        </div>
      );
    }
    return null;
  }

  renderPopover() {
    let placement = "bottom-start";
    let hideArrow = false;
    let alwaysPortal = false;
    if (isTablet) {
      placement = "bottom-end";
    }

    let popoverOptions = {
      className: `inspectorPopover`,
      placement,
      hideArrow,
      isOpen: this.state.showMenu,
      target: this._dropdownId,
      toggle: this.toggle,
      boundariesElement: "viewport"
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggle}
            backText={this.props.backText ? this.props.backText : "Back"}
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  renderControl() {
    const selectedOptions = this.getSelectedOptions(this.props);
    const itemLabel = renderOptionsLabel(selectedOptions);
    const arrowIcon = this.props.arrowIcon
      ? this.props.arrowIcon
      : this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode)
        ? "rightArrow"
        : "downArrow";
    return (
      <div
        className={`dropdownControl ${this.state.showMenu && !this.props.disabled ? "open" : "closed"}`}
        onClick={this.props.disabled || !this.props.options.size ? null : this.toggle}
      >
        <div className="dropdownText">
          <div
            className={`dropdownLabel ${this.props.centerLabel ? "centered" : ""} ${
              !this.props.options.size || !itemLabel.length ? "empty" : ""
            }`}
          >
            {!this.props.options.size ? "None" : itemLabel.length ? itemLabel : "No selection"}
          </div>
        </div>
        <Icon className="downArrow" icon={arrowIcon} color="mono" />
      </div>
    );
  }

  render() {
    return (
      <div className="formDropdown formCheckboxDropdown">
        {this.props.label ? <span className="formDropdownLabel">{this.props.label}</span> : null}
        <div
          ref={node => (this._domNode = node)}
          className={`dropdown ${this.props.disabled ? "disabled" : ""}`}
          tabIndex={this.props.disabled ? -1 : this.props.tabIndex ? this.props.tabIndex : 0}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          id={this._dropdownId}
        >
          {this.renderControl()}
        </div>
        {this.state.showMenu ? <div className="formListBackdrop" onClick={this.hideMenu} /> : null}
        {this.state.showMenu ? this.renderPopover() : null}
      </div>
    );
  }
}

FormCheckboxDropdown.propTypes = {
  options: PropTypes.oneOfType([PropTypeShapes.listOptionArrayOrList, PropTypes.func]).isRequired,
  openOnFocus: PropTypes.bool,
  allowSearch: PropTypes.bool,
  label: PropTypes.string,
  centerLabel: PropTypes.bool,
  preserveSpaces: PropTypes.bool,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  popoverClassName: PropTypes.string,
  onToggleMenu: PropTypes.func,
  initialShowMenu: PropTypes.bool,
  title: PropTypes.string,
  backText: PropTypes.string,
  showDescription: PropTypes.bool,
  useParentWidth: PropTypes.bool.isRequired,
  style: PropTypes.object,
  disableTabLock: PropTypes.bool,

  //#!if iOS === true || harvard === true
  alwaysPortal: PropTypes.bool,
  showTick: PropTypes.bool,
  arrowIcon: PropTypes.string,

  // From withScreenContext
  screenContext: PropTypes.object,

  // From withContainerContext
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
  //#!endif
};

FormCheckboxDropdown.defaultProps = {
  useParentWidth: true,
  showTick: true
};

let exportedView = FormCheckboxDropdown;
//#!if iOS === true || harvard === true
exportedView = withContainerContext(withScreenContext(FormCheckboxDropdown));
//#!endif
export default exportedView;
